import { Skeleton } from '@/components/ui/skeleton'
import { getLateEmployees } from '@/modules/Dashboard/services'
import { LateEmployee } from '@/modules/Dashboard/types'
import { generatePathDatacentral, paths } from '@/utils/path'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import dayjs, { Dayjs } from 'dayjs'
import 'dayjs/locale/vi'
import isoWeek from 'dayjs/plugin/isoWeek'
import {
    Clock,
    User,
    Building2,
    AlertTriangle,
    FileText,
    Plane,
} from 'lucide-react'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Button, Typogaphy } from 'vnetwork-ui'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import WeekCalendar from '../../../shared/WeekCalendar'

dayjs.extend(isoWeek)
dayjs.locale('vi')

const LateEmployeesSkeleton = () => (
    <div className="space-y-3">
        {[1, 2, 3].map((i) => (
            <div key={i} className="p-3 border rounded-lg space-y-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
            </div>
        ))}
    </div>
)

// Calculate late minutes based on work_day_option
const calculateLateMinutes = (employee: LateEmployee): number => {
    const [hours, minutes] = employee.work_day_in.split(':').map(Number)
    const checkInMinutes = hours * 60 + minutes
    // work_day_option: 0=morning, 1=afternoon, 2=all day, null=morning
    const isMorningShift =
        employee.work_day_option === 0 ||
        employee.work_day_option === 2 ||
        employee.work_day_option === null
    const lateThreshold = isMorningShift ? 8 * 60 + 30 : 13 * 60 // 08:30 or 13:00
    return Math.max(0, checkInMinutes - lateThreshold)
}

// Get shift label for employee
const getShiftLabel = (
    employee: LateEmployee,
    t: (key: string) => string
): string | null => {
    if (employee.day_off?.day_off_item_option) {
        if (employee.day_off.day_off_item_option === 'morning')
            return t('late_employees.shift.morning')
        if (employee.day_off.day_off_item_option === 'afternoon')
            return t('late_employees.shift.afternoon')
        if (employee.day_off.day_off_item_option === 'allDay')
            return t('late_employees.shift.all_day')
    }
    if (employee.business?.business_option) {
        if (employee.business.business_option === '0')
            return t('late_employees.shift.morning')
        if (employee.business.business_option === '1')
            return t('late_employees.shift.afternoon')
        if (employee.business.business_option === '2')
            return t('late_employees.shift.all_day')
    }
    return null
}

// Check if dayoff/business ticket is pending (not approved)
const isTicketPending = (
    employee: LateEmployee
): { dayoffPending: boolean; businessPending: boolean } => {
    const approvedStatuses = ['approved', 'leader-approved']
    const dayoffPending = employee.day_off
        ? !approvedStatuses.includes(employee.day_off.day_off_status)
        : false
    const businessPending = employee.business
        ? !approvedStatuses.includes(employee.business.business_status)
        : false
    return { dayoffPending, businessPending }
}

interface DepartmentGroupProps {
    departmentName: string
    employees: LateEmployee[]
}

const DepartmentGroup: React.FC<DepartmentGroupProps> = ({
    departmentName,
    employees,
}) => {
    const { t } = useTranslation('dashboard')

    const getLateColor = (lateMinutes: number) => {
        if (lateMinutes < 15)
            return 'bg-yellow-50 text-yellow-700 border-yellow-200'
        if (lateMinutes < 30)
            return 'bg-orange-50 text-orange-700 border-orange-200'
        return 'bg-red-50 text-red-700 border-red-200'
    }

    // Calculate total late minutes for department
    const departmentLateMinutes = employees.reduce((sum, emp) => {
        return sum + calculateLateMinutes(emp)
    }, 0)

    return (
        <div className="mb-3">
            {/* Department Header */}
            <div className="flex items-center gap-1.5 mb-1.5">
                <Building2 className="w-3 h-3 text-purple-600" />
                <Typogaphy className="text-xs font-semibold text-gray-800">
                    {departmentName || 'Chưa phân phòng'}
                </Typogaphy>
                <span className="text-[10px] bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded-full">
                    {employees.length}
                </span>
                <span className="text-[10px] bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded-full">
                    +{departmentLateMinutes}p
                </span>
            </div>

            {/* Employee List */}
            <div className="space-y-1 pl-4">
                {employees.map((employee) => {
                    const lateMinutes = calculateLateMinutes(employee)
                    const hasDayoff = !!employee.day_off
                    const hasBusiness = !!employee.business
                    const shiftLabel = getShiftLabel(employee, t)
                    const { dayoffPending, businessPending } =
                        isTicketPending(employee)

                    return (
                        <div
                            key={employee.work_day_id}
                            className={`flex items-center justify-between px-2 py-1.5 rounded border cursor-pointer hover:shadow-sm transition-all ${getLateColor(lateMinutes)}`}
                        >
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5">
                                    <User className="w-3 h-3 flex-shrink-0" />
                                    <Typogaphy className="text-xs font-medium truncate capitalize">
                                        {employee.employee_name}
                                    </Typogaphy>
                                </div>
                                <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                                    <Typogaphy className="text-[9px] opacity-70">
                                        {employee.position_name}
                                    </Typogaphy>
                                    {/* Dayoff/Business ticket badge */}
                                    {hasDayoff && (
                                        <span
                                            className={`inline-flex items-center gap-0.5 text-[8px] px-1 py-0.5 rounded ${
                                                dayoffPending
                                                    ? 'bg-yellow-100 border border-yellow-400'
                                                    : 'bg-orange-100/50'
                                            }`}
                                        >
                                            <FileText className="w-2 h-2" />
                                            {t('late_employees.dayoff')}
                                            {shiftLabel && (
                                                <span className="opacity-70">
                                                    ({shiftLabel})
                                                </span>
                                            )}
                                            {dayoffPending && (
                                                <span className="text-[7px] bg-yellow-300 text-yellow-900 px-0.5 rounded ml-0.5">
                                                    {t(
                                                        'late_employees.pending'
                                                    )}
                                                </span>
                                            )}
                                        </span>
                                    )}
                                    {hasBusiness && (
                                        <span
                                            className={`inline-flex items-center gap-0.5 text-[8px] px-1 py-0.5 rounded ${
                                                businessPending
                                                    ? 'bg-yellow-100 border border-yellow-400'
                                                    : 'bg-purple-100/50'
                                            }`}
                                        >
                                            <Plane className="w-2 h-2" />
                                            {t('late_employees.business')}
                                            {shiftLabel && (
                                                <span className="opacity-70">
                                                    ({shiftLabel})
                                                </span>
                                            )}
                                            {businessPending && (
                                                <span className="text-[7px] bg-yellow-300 text-yellow-900 px-0.5 rounded ml-0.5">
                                                    {t(
                                                        'late_employees.pending'
                                                    )}
                                                </span>
                                            )}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                                <div className="text-right">
                                    <div className="flex items-center gap-0.5">
                                        <Clock className="w-2.5 h-2.5" />
                                        <span className="text-[10px] font-bold">
                                            {employee.work_day_in}
                                        </span>
                                    </div>
                                    <span className="text-[9px] opacity-70">
                                        +{lateMinutes}p
                                    </span>
                                </div>
                            </div>
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

// Helper to get default selected date (skip Sunday - if today is Monday, select Friday)
const getDefaultSelectedDate = (): Date => {
    const today = dayjs()
    const yesterday = today.subtract(1, 'day')
    // If yesterday is Sunday (day 0), select Friday (2 days before)
    if (yesterday.day() === 0) {
        return yesterday.subtract(2, 'day').toDate()
    }
    return yesterday.toDate()
}

const LateEmployeesWidget: React.FC = () => {
    const { t } = useTranslation('dashboard')
    const navigate = useNavigate()
    const today = dayjs()

    // Default to yesterday, but if yesterday is Sunday, select Friday
    const [selectedDate, setSelectedDate] = useState<Date>(() =>
        getDefaultSelectedDate()
    )

    // Week start - default to previous week if today is Monday, otherwise current week
    const [weekStart, setWeekStart] = useState<Dayjs>(() => {
        const isMonday = today.isoWeekday() === 1
        return isMonday
            ? today.subtract(1, 'week').startOf('isoWeek')
            : today.startOf('isoWeek')
    })

    const handlePrevWeek = () => {
        const newWeekStart = weekStart.subtract(1, 'week')
        setWeekStart(newWeekStart)
        // Select Saturday (last day Mon-Sat) of new week, unless it's in the future
        const saturday = newWeekStart.add(5, 'day')
        if (saturday.isBefore(today, 'day') || saturday.isSame(today, 'day')) {
            setSelectedDate(saturday.toDate())
        } else {
            setSelectedDate(getDefaultSelectedDate())
        }
    }

    const handleNextWeek = () => {
        const newWeekStart = weekStart.add(1, 'week')
        if (
            newWeekStart.isBefore(today, 'day') ||
            newWeekStart.isSame(today, 'day')
        ) {
            setWeekStart(newWeekStart)
            setSelectedDate(newWeekStart.toDate())
        }
    }

    // Fetch data for selected date only
    const dateRange = useMemo(
        () => ({
            gte: dayjs(selectedDate).format('YYYY-MM-DD'),
            lte: dayjs(selectedDate).format('YYYY-MM-DD'),
        }),
        [selectedDate]
    )

    const { data, renderState } = useQueryWithHandler(
        {
            queryKey: [QUERY_KEYS.LATE_EMPLOYEES, dateRange.gte, dateRange.lte],
            queryFn: () => getLateEmployees(dateRange),
            staleTime: 5 * 60 * 1000,
        },
        {
            loadingComponent: <LateEmployeesSkeleton />,
            errorDisplayMode: 'page',
        }
    )

    // Group by department
    const groupedByDepartment = useMemo(() => {
        const employees = data || []
        const grouped: Record<string, LateEmployee[]> = {}

        employees.forEach((emp: LateEmployee) => {
            const deptKey = emp.department_name || 'unknown'
            if (!grouped[deptKey]) {
                grouped[deptKey] = []
            }
            grouped[deptKey].push(emp)
        })

        // Sort by department name and employees by check-in time
        return Object.entries(grouped)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([department, employees]) => ({
                department,
                employees: employees.sort((a, b) =>
                    a.work_day_in.localeCompare(b.work_day_in)
                ),
            }))
    }, [data])

    const stateComponent = renderState()

    const handleViewAll = () => {
        navigate(generatePathDatacentral(paths.workdayHoliday, {}))
    }

    const totalLateCount = data?.length || 0

    // Calculate total late minutes for selected day (using work_day_option)
    const totalLateMinutes = useMemo(() => {
        if (!data || data.length === 0) return 0
        return data.reduce((sum: number, emp: LateEmployee) => {
            return sum + calculateLateMinutes(emp)
        }, 0)
    }, [data])

    return (
        <div className="relative h-full flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-500" />
                    <Typogaphy className="font-semibold text-primary text-sm">
                        {t('late_employees.title')}
                    </Typogaphy>
                </div>
                {totalLateCount > 0 && (
                    <div
                        className="flex items-center gap-2"
                        data-tour="late-employees-stats"
                    >
                        <span className="text-[10px] text-white bg-red-500 px-1.5 py-0.5 rounded-full">
                            {totalLateCount} {t('late_employees.people')}
                        </span>
                        <span className="text-[10px] text-white bg-orange-500 px-1.5 py-0.5 rounded-full">
                            +{totalLateMinutes} {t('late_employees.minutes')}
                        </span>
                    </div>
                )}
            </div>

            {/* Week Calendar */}
            <div data-tour="late-employees-calendar">
                <WeekCalendar
                    selectedDate={selectedDate}
                    setSelectedDate={setSelectedDate}
                    weekStart={weekStart}
                    onPrevWeek={handlePrevWeek}
                    onNextWeek={handleNextWeek}
                    includeSunday={false}
                    disableFuture={true}
                />
            </div>

            {/* Content */}
            <div className="min-h-[280px] max-h-[280px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                {stateComponent ? (
                    stateComponent
                ) : totalLateCount > 0 ? (
                    <div>
                        {groupedByDepartment.map(
                            ({ department, employees }) => (
                                <DepartmentGroup
                                    key={department}
                                    departmentName={department}
                                    employees={employees}
                                />
                            )
                        )}
                    </div>
                ) : (
                    <div className="text-center py-6">
                        <div className="w-10 h-10 mx-auto mb-3 bg-green-100 rounded-full flex items-center justify-center">
                            <Clock className="w-5 h-5 text-green-500" />
                        </div>
                        <Typogaphy className="text-gray-500 text-xs font-medium mb-1">
                            {t('late_employees.no_late')}
                        </Typogaphy>
                        <Typogaphy className="text-gray-400 text-[10px]">
                            {t('late_employees.no_late_description')}
                        </Typogaphy>
                    </div>
                )}
            </div>

            {/* View All Button */}
            <div className="mt-2 pt-2 border-t border-gray-100">
                <Button
                    size="small"
                    variant="text"
                    className="w-full !bg-bg-secondary-10 text-primary transition-colors text-xs"
                    onClick={handleViewAll}
                >
                    {t('late_employees.view_all')}
                </Button>
            </div>
        </div>
    )
}

export default LateEmployeesWidget
